// src/components/analytics/RecentMovementsTable.jsx
// Panneau autonome : live feed + recherche Tail/Date + totaux fees + export XLSX
// Fenêtre fixe — ne scroll pas avec la page.
// Quand une recherche est active, affiche les résultats ; sinon reprend le live feed.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Activity, Search, X, Download, RefreshCw, Loader,
  ArrowUpRight, ArrowDownLeft, CheckCircle, AlertCircle,
  ChevronRight, DollarSign, Tag, Receipt, Shield
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtTime = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString([], {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const fmtMoney = (v) =>
  v == null ? '—' : `$${Number(v).toFixed(2)}`;

const weightBadge = (cls) => {
  const map = {
    Light:         'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    Medium:        'bg-amber-500/15   text-amber-400   border-amber-500/30',
    Heavy:         'bg-orange-500/15  text-orange-400  border-orange-500/30',
    'Super Heavy': 'bg-red-500/15     text-red-400     border-red-500/30',
  };
  return map[cls] || 'bg-gray-700/50 text-gray-400 border-gray-600';
};

// ─── Export XLSX ──────────────────────────────────────────────────────────────

const exportXLSX = (movements, totals) => {
  // Génère un CSV BOM-utf8 téléchargeable (ouvre nativement dans Excel)
  const cols = [
    'Tail Number', 'Source', 'Movement', 'Aircraft Type', 'Engine Type',
    'MTOW (lbs)', 'Weight Class', 'Operator', 'Fee (net)', 'Fee (gross)',
    'Tax', 'Exempt', 'Speed (kts)', 'Billable', 'Timestamp'
  ];
  const rows = movements.map(m => [
    m.tail_number,
    m.source,
    m.movement_type,
    m.aircraft_type || '',
    m.engine_type   || '',
    m.mtow_lbs      != null ? m.mtow_lbs : '',
    m.weight_class  || '',
    m.operator_name || '',
    m.fee           != null ? m.fee       : '',
    m.fee_gross     != null ? m.fee_gross : '',
    m.fee_tax       != null ? m.fee_tax   : '',
    m.fee_exempt ? 'Yes' : 'No',
    m.speed != null ? Math.round(m.speed) : '',
    m.is_billable ? 'Yes' : 'No',
    m.timestamp || '',
  ]);

  // Ajouter une ligne de totaux
  rows.push([]);
  rows.push(['TOTALS', '', '', '', '', '', '', '',
    totals.total_net   || 0,
    totals.total_gross || 0,
    totals.total_tax   || 0,
    `${totals.total_exempt || 0} exempt`, '', '',
    `${totals.count || 0} movements`
  ]);

  const csvContent = [cols, ...rows]
    .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const BOM    = '\uFEFF';
  const blob   = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url    = URL.createObjectURL(blob);
  const link   = document.createElement('a');
  const today  = new Date().toISOString().split('T')[0];
  link.href     = url;
  link.download = `movements_${today}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

// ─── Totals Bar ───────────────────────────────────────────────────────────────

const TotalsBar = ({ totals, searchMode }) => {
  if (!totals || !searchMode) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-4 py-2.5 bg-gray-900/60 border-t border-gray-700/50">
      <div className="flex items-center gap-2">
        <Receipt size={12} className="text-gray-400 shrink-0" />
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Mouvements</p>
          <p className="text-sm font-bold text-white tabular-nums">{totals.count ?? '—'}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <DollarSign size={12} className="text-emerald-400 shrink-0" />
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Fees Net TTC</p>
          <p className="text-sm font-bold text-emerald-400 tabular-nums">{fmtMoney(totals.total_net)}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Tag size={12} className="text-sky-400 shrink-0" />
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Taxe</p>
          <p className="text-sm font-bold text-sky-400 tabular-nums">{fmtMoney(totals.total_tax)}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Shield size={12} className="text-amber-400 shrink-0" />
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Exonérés</p>
          <p className="text-sm font-bold text-amber-400 tabular-nums">{totals.total_exempt ?? 0}</p>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const RecentMovementsTable = ({ filterType = 'all', onSelectTail }) => {
  // Live feed
  const [liveFeed,    setLiveFeed]    = useState([]);
  const [liveLoading, setLiveLoading] = useState(true);

  // Search
  const [searchTail,  setSearchTail]  = useState('');
  const [dateFrom,    setDateFrom]    = useState('');
  const [dateTo,      setDateTo]      = useState('');
  const [searchMode,  setSearchMode]  = useState(false);
  const [searchRows,  setSearchRows]  = useState([]);
  const [searchTotals,setSearchTotals]= useState(null);
  const [searching,   setSearching]   = useState(false);

  const liveIntervalRef = useRef(null);

  // ── Live feed ───────────────────────────────────────────────────────────────
  const fetchLive = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/tracking/movements/recent?limit=50`);
      setLiveFeed(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Live feed error:', err);
    } finally {
      setLiveLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLive();
    liveIntervalRef.current = setInterval(fetchLive, 30000);
    return () => clearInterval(liveIntervalRef.current);
  }, [fetchLive]);

  // ── Search ──────────────────────────────────────────────────────────────────
  const runSearch = useCallback(async () => {
    if (!searchTail && !dateFrom && !dateTo) {
      clearSearch();
      return;
    }
    setSearching(true);
    setSearchMode(true);
    try {
      const params = {};
      if (searchTail) params.tail      = searchTail.trim();
      if (dateFrom)   params.date_from = dateFrom;
      if (dateTo)     params.date_to   = dateTo;
      params.limit = 200;

      const res = await axios.get(`${API_BASE}/tracking/movements/search`, { params });
      setSearchRows(res.data.movements  || []);
      setSearchTotals(res.data.totals   || null);
    } catch (err) {
      console.error('Search error:', err);
      setSearchRows([]);
      setSearchTotals(null);
    } finally {
      setSearching(false);
    }
  }, [searchTail, dateFrom, dateTo]);

  const clearSearch = () => {
    setSearchTail('');
    setDateFrom('');
    setDateTo('');
    setSearchMode(false);
    setSearchRows([]);
    setSearchTotals(null);
  };

  // Recherche auto sur frappe (debounce 600ms)
  const debounceRef = useRef(null);
  useEffect(() => {
    if (!searchTail && !dateFrom && !dateTo) {
      clearSearch();
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runSearch, 600);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTail, dateFrom, dateTo]);

  // ── Data à afficher ─────────────────────────────────────────────────────────
  const displayRows = searchMode ? searchRows : liveFeed;
  const filtered    = filterType === 'all'
    ? displayRows
    : displayRows.filter(m => m.source === filterType);

  const isLoading = searchMode ? searching : liveLoading;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col" style={{ height: '420px' }}>

      {/* ── Barre de recherche ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 px-1 pb-3">
        {/* Tail */}
        <div className="relative flex-1 min-w-[120px]">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
          <input
            type="text"
            value={searchTail}
            onChange={e => setSearchTail(e.target.value)}
            placeholder="Tail number…"
            className="w-full pl-7 pr-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg
                       text-xs text-white placeholder-gray-500 focus:outline-none
                       focus:border-sky-500 transition-colors"
          />
        </div>

        {/* Date From */}
        <input
          type="date"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          className="px-2 py-1.5 bg-gray-800 border border-gray-700 rounded-lg
                     text-xs text-gray-300 focus:outline-none focus:border-sky-500 transition-colors"
        />
        <span className="text-gray-600 text-xs">→</span>
        {/* Date To */}
        <input
          type="date"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          className="px-2 py-1.5 bg-gray-800 border border-gray-700 rounded-lg
                     text-xs text-gray-300 focus:outline-none focus:border-sky-500 transition-colors"
        />

        {/* Clear */}
        {searchMode && (
          <button
            onClick={clearSearch}
            className="flex items-center gap-1 px-2 py-1.5 bg-gray-700/60 hover:bg-gray-700
                       border border-gray-600 rounded-lg text-xs text-gray-400 hover:text-white transition-colors"
            title="Effacer la recherche"
          >
            <X size={11} /> Clear
          </button>
        )}

        {/* Export */}
        {searchMode && searchRows.length > 0 && (
          <button
            onClick={() => exportXLSX(searchRows, searchTotals || {})}
            className="flex items-center gap-1 px-2 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20
                       border border-emerald-500/30 rounded-lg text-xs text-emerald-400 transition-colors"
            title="Exporter en CSV/Excel"
          >
            <Download size={11} /> Export
          </button>
        )}

        {/* Statut */}
        <div className="ml-auto flex items-center gap-1.5 text-xs">
          {isLoading
            ? <><Loader size={11} className="animate-spin text-sky-400" /><span className="text-gray-500">Loading…</span></>
            : searchMode
              ? <span className="text-sky-400">{filtered.length} résultat{filtered.length !== 1 ? 's' : ''}</span>
              : <span className="text-gray-500 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Live feed
                </span>
          }
        </div>
      </div>

      {/* ── Table (scroll interne) ──────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto overflow-x-auto">
        {isLoading && filtered.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <Loader size={18} className="animate-spin text-sky-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-sm">
            {searchMode ? 'Aucun résultat pour cette recherche.' : 'En attente de mouvements…'}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-gray-850">
              <tr className="text-gray-500 border-b border-gray-700 bg-gray-900/80 backdrop-blur">
                <th className="text-left py-2 px-3 whitespace-nowrap">Tail</th>
                <th className="text-left py-2 px-3 whitespace-nowrap">Source</th>
                <th className="text-left py-2 px-3 whitespace-nowrap">Movement</th>
                <th className="text-left py-2 px-3 whitespace-nowrap">Type</th>
                <th className="text-left py-2 px-3 whitespace-nowrap">MTOW</th>
                <th className="text-left py-2 px-3 whitespace-nowrap">Class</th>
                <th className="text-left py-2 px-3 whitespace-nowrap">Fee</th>
                <th className="text-left py-2 px-3 whitespace-nowrap">Operator</th>
                <th className="text-left py-2 px-3 whitespace-nowrap">Timestamp</th>
                <th className="text-left py-2 px-3 whitespace-nowrap">Billable</th>
                <th className="py-2 px-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/40">
              {filtered.map((m, i) => (
                <tr
                  key={`${m.visit_id ?? i}-${m.timestamp}`}
                  className="hover:bg-gray-700/30 transition-colors cursor-pointer group"
                  onClick={() => onSelectTail && onSelectTail(m.tail_number)}
                >
                  <td className="py-2 px-3 font-mono font-bold text-white whitespace-nowrap">{m.tail_number}</td>

                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      m.source === 'visitor'
                        ? 'bg-sky-500/20 text-sky-400'
                        : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      {m.source === 'visitor' ? 'Visitor' : 'Client'}
                    </span>
                  </td>

                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className={`flex items-center gap-1 font-medium ${
                      m.movement_type === 'TAKEOFF' ? 'text-emerald-400' : 'text-orange-400'
                    }`}>
                      {m.movement_type === 'TAKEOFF'
                        ? <><ArrowUpRight size={12} />Kick-off</>
                        : <><ArrowDownLeft size={12} />Landing</>}
                    </span>
                  </td>

                  <td className="py-2 px-3 text-gray-300 whitespace-nowrap">
                    {m.aircraft_type || <span className="text-gray-600">—</span>}
                  </td>

                  <td className="py-2 px-3 text-gray-300 tabular-nums whitespace-nowrap">
                    {m.mtow_lbs != null
                      ? `${Number(m.mtow_lbs).toLocaleString()} lbs`
                      : m.mtow_kg
                        ? `${Math.round(m.mtow_kg * 2.20462).toLocaleString()} lbs`
                        : <span className="text-gray-600">—</span>}
                  </td>

                  <td className="py-2 px-3 whitespace-nowrap">
                    {m.weight_class && m.weight_class !== 'Unknown'
                      ? <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${weightBadge(m.weight_class)}`}>
                          {m.weight_class}
                        </span>
                      : <span className="text-gray-600">—</span>
                    }
                  </td>

                  <td className="py-2 px-3 tabular-nums whitespace-nowrap">
                    {m.fee_exempt
                      ? <span className="text-amber-400 text-[10px] font-medium flex items-center gap-1">
                          <Shield size={10} />Exempt
                        </span>
                      : m.fee > 0
                        ? <span className="text-emerald-400 font-medium">{fmtMoney(m.fee)}</span>
                        : <span className="text-gray-600">—</span>
                    }
                  </td>

                  <td className="py-2 px-3 text-gray-300 max-w-[130px] truncate">
                    {m.operator_name || <span className="text-gray-600 italic">Unregistered</span>}
                  </td>

                  <td className="py-2 px-3 text-gray-400 tabular-nums whitespace-nowrap">{fmtTime(m.timestamp)}</td>

                  <td className="py-2 px-3">
                    {m.is_billable
                      ? <CheckCircle size={13} className="text-emerald-400" />
                      : <AlertCircle size={13} className="text-gray-600" />
                    }
                  </td>

                  <td className="py-2 px-3">
                    <ChevronRight
                      size={13}
                      className="text-gray-600 group-hover:text-sky-400 transition-colors"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Totals bar (search mode only) ──────────────────────────────────── */}
      <TotalsBar totals={searchTotals} searchMode={searchMode} />
    </div>
  );
};

export default RecentMovementsTable;